import { Credentials } from '@aws-amplify/core'
import { CognitoAccessToken, CognitoIdToken, CognitoRefreshToken } from 'amazon-cognito-identity-js'
import { Amplify, Auth } from 'aws-amplify'
import {
  COGNITO_SCOPES,
  HOSTED_UI_DOMAIN,
  IMPERSONATION_COOKIE_NAME,
  MARQII_COGNITO_CLIENT_ID,
  SSO_OAUTH_CREDS,
} from '../../src/utils/constants/oauth'

const DEV_MOCKED_USER_ID = 'dev-local-user-id'
const DEV_MOCKED_EMAIL = 'dev@marqii.com'

const getCookieValue = (name: string) =>
  document.cookie
    .split('; ')
    .filter((row) => row.startsWith(name))
    .map((c) => c.split('=')[1])[0]

const isImpersonating = getCookieValue(IMPERSONATION_COOKIE_NAME)

const customClientId = getCookieValue(MARQII_COGNITO_CLIENT_ID)
const customClientIdOauthUrls =
  SSO_OAUTH_CREDS.has(customClientId) && SSO_OAUTH_CREDS.get(customClientId)

const oauthSettings = customClientIdOauthUrls
  ? {
      oauth: {
        domain: HOSTED_UI_DOMAIN,
        scopes: COGNITO_SCOPES,
        redirectSignIn: customClientIdOauthUrls.signInRedirectUrl,
        redirectSignOut: customClientIdOauthUrls.signOutUrl,
        responseTypes: 'code',
      },
    }
  : {}

export const configureCognito = () => {
  const region = 'us-east-1'
  const userPoolId = 'us-east-1_RvmkA3ZEL'
  const userPoolWebClientId = isImpersonating
    ? '18ugf0n304151b7a5tv3dt45p4'
    : customClientId || '4af42doicu3r2g2ld0su841m2l'

  Amplify.configure({
    Auth: {
      // Amazon Cognito Region
      region,

      // Amazon Cognito User Pool ID
      userPoolId,

      // Amazon Cognito Web Client ID (26-char alphanumeric string)
      userPoolWebClientId,

      // maybe add oauth settings for sso
      ...oauthSettings,
    },
  })

  if (process.env.NODE_ENV === 'development') {
    // If working offline, send a hardcoded identity
    Credentials.get = () => Promise.resolve('pizza')
    Auth.currentUserInfo = () =>
      Promise.resolve({
        attributes: {
          sub: DEV_MOCKED_USER_ID,
        },
      })

    Auth.currentSession = () =>
      Promise.resolve({
        getAccessToken: () => new CognitoAccessToken({ AccessToken: 'testAccessToken' }),
        getIdToken: () => new CognitoIdToken({ IdToken: 'testIdToken' }),
        getRefreshToken: () => new CognitoRefreshToken({ RefreshToken: 'testRefreshToken' }),
        isValid: () => true,
      })

    Auth.signIn = () =>
      Promise.resolve({
        attributes: {
          'custom:tenantId': 'accountId',
          sub: DEV_MOCKED_USER_ID,
          given_name: 'Fake',
          family_name: 'Name',
          name: 'Fake Name',
          email: DEV_MOCKED_EMAIL,
        },
        getSignInUserSession: () => ({
          getAccessToken: () => ({
            getJwtToken: () => 'jwtAccessToken',
            decodePayload: () => ({ 'cognito:groups': ['accountId'] }),
          }),
          getIdToken: () => ({
            getJwtToken: () => 'jwtIdToken',
          }),
        }),
      })

    Auth.currentAuthenticatedUser = () =>
      Promise.resolve({
        attributes: {
          'custom:tenantId': 'accountId',
          sub: DEV_MOCKED_USER_ID,
          given_name: 'Fake',
          family_name: 'Name',
          name: 'Fake Name',
          email: DEV_MOCKED_EMAIL,
        },
        getSignInUserSession: () => ({
          getAccessToken: () => ({
            getJwtToken: () => 'jwtAccessToken',
            decodePayload: () => ({ 'cognito:groups': ['accountId'] }),
          }),
          getIdToken: () => ({
            getJwtToken: () => 'jwtIdToken',
          }),
        }),
      })

    Auth.changePassword = () => {
      const hasError = Math.random() < 0.5
      return hasError ? Promise.reject(new Error('ERROR!!')) : Promise.resolve('SUCCESS')
    }

    Auth.updateUserAttributes = () => {
      return Promise.resolve('SUCCESS')
    }

    Auth.forgotPassword = () =>
      Promise.resolve({
        CodeDeliveryDetails: {
          AttributeName: 'email',
          DeliveryMedium: 'EMAIL',
          Destination: 'l***@e***',
        },
      })

    Auth.forgotPasswordSubmit = () => Promise.resolve('SUCCESS')

    Auth.completeNewPassword = () => Promise.resolve('SUCCESS')
  }
}
